import React from "react";
import { motion } from "framer-motion";
import { AlertTriangle, Check, Loader2 } from "lucide-react";
import ModelIcon from "./ModelIcon";
import ResponseContent from "./ResponseContent";
import ResponseActions from "./ResponseActions";
import "./Dashboard.css";

/**
 * ResponseCard - Complete response card with content and actions
 * @param {string} modelName - Name of the AI model
 * @param {string} htmlContent - HTML content to display
 * @param {boolean} isLoading - Whether response is loading
 * @param {boolean} hasError - Whether there's an error
 * @param {string} errorMessage - Error message
 * @param {boolean} disabled - Whether actions should be disabled
 * @param {Function} onEdit - Callback for Edit/Export button
 * @param {Function} onRemove - Callback for Remove button
 * @param {Object} contentRef - Ref for content div
 * @param {Object} variants - Framer motion variants
 * @param {number} index - Index for animation delay
 * @param {boolean} plain - renders one continuous block of text at natural
 *   height instead of the collapsible, fixed-height card body.
 */
export default function ResponseCard({
  modelName,
  htmlContent = "",
  isGridLoading = false,
  isLoading = false,
  hasError = false,
  errorMessage = "",
  disabled = false,
  onEdit,
  onRemove,
  contentRef = null,
  variants = null,
  index = 0,
  editLabel = "Edit / Export",
  plain = false
}) {
  const itemVariants = variants?.item || {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const hasContent = htmlContent.trim() !== "";

  // Status shown in the card header. The conditions mirror ResponseActions
  // exactly, so which controls are available is unchanged.
  const status = hasError
    ? { label: "Failed", tone: "failed", Icon: AlertTriangle, spin: false }
    : isLoading
      ? { label: "Analysing", tone: "active", Icon: Loader2, spin: true }
      : isGridLoading && hasContent
        ? { label: "Complete", tone: "complete", Icon: Check, spin: false }
        : null;

  return (
    <motion.div
      className="response-card"
      data-model={modelName}
      variants={itemVariants}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ delay: index * 0.1, type: "spring", stiffness: 300, damping: 20 }}
    >
      <div className="response-card-content">
        <header className="rs-card-head">
          <span className="rs-card-identity">
            <span className="rs-card-badge" aria-hidden="true">
              <ModelIcon model={modelName} size={17} />
            </span>
            <span className="rs-card-titles">
              <h2 className="response-title">{modelName}</h2>
              <span className="rs-card-subtitle">Response analysis</span>
            </span>
          </span>

          {status && (
            <motion.span
              key={status.label}
              className={`rs-card-status rs-phase-${status.tone}`}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
            >
              <status.Icon size={12} className={status.spin ? "ds-spin" : undefined} />
              {status.label}
            </motion.span>
          )}
        </header>

        <ResponseContent
          htmlContent={htmlContent}
          isLoading={isLoading}
          modelName={modelName}
          hasError={hasError}
          errorMessage={errorMessage}
          contentRef={contentRef}
          plain={plain}
        />
        <ResponseActions
          disabled={disabled}
          hasContent={hasContent}
          isLoading={isLoading}
          isGridLoading={isGridLoading}
          onEdit={onEdit}
          onRemove={onRemove}
          editLabel={editLabel}
        />
      </div>
    </motion.div>
  );
}
